/**
 * Wave 19 — SSE + poll overlays for locations and events (tip599 parity).
 * Prefer SSE push; keep poll as continuous fallback; reconnect SSE with
 * exponential backoff (industry ops-desk pattern). Never invent pins/events.
 */
import { useEffect, useState } from "react";
import { apiBase, path, usePolled } from "./api.js";

const BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 30000];

/**
 * @param {object} opts
 * @param {string} opts.streamOp contract operation_id for SSE
 * @param {object} [opts.streamQuery]
 * @param {string|null} opts.pollPath contract path string for GET poll
 * @param {number} [opts.everyMs]
 * @param {(event: string, data: string, set: Function) => void} opts.onFrame
 */
function useSseWithPoll({ streamOp, streamQuery, pollPath, everyMs = 20000, onFrame }) {
  const poll = usePolled(pollPath, everyMs);
  const [overlay, setOverlay] = useState(null);
  const [transport, setTransport] = useState("poll");

  useEffect(() => {
    if (!streamOp) return undefined;
    let dead = false;
    let ac = null;
    let retryTimer = null;
    let attempt = 0;

    const connect = async () => {
      if (dead) return;
      ac = new AbortController();
      const url = apiBase() + path(streamOp, { query: streamQuery || undefined });
      try {
        const r = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "text/event-stream",
            "Cache-Control": "no-store",
          },
          signal: ac.signal,
        });
        if (!r.ok || !r.body || typeof r.body.getReader !== "function") {
          throw new Error(String(r.status || "stream unsupported"));
        }
        attempt = 0;
        if (!dead) setTransport("sse");
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (!dead) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const chunk of parts) {
            let event = "message";
            let data = "";
            for (const line of chunk.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
            }
            if (event === "ping" || event === "error" || !data) continue;
            try {
              onFrame(event, data, (next) => {
                if (!dead) setOverlay(next);
              });
            } catch (_) { /* ignore malformed */ }
          }
        }
        if (!dead) setTransport("poll");
      } catch (e) {
        if (!dead && !(e && e.name === "AbortError")) setTransport("poll");
      }
      if (!dead) {
        const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
        attempt += 1;
        retryTimer = setTimeout(connect, wait);
      }
    };

    connect();
    return () => {
      dead = true;
      if (ac) ac.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [streamOp, everyMs, JSON.stringify(streamQuery || {})]);

  return {
    data: overlay != null ? overlay : poll.data,
    error: overlay != null ? "" : (poll.error || ""),
    loading: overlay == null && poll.loading,
    refreshing: poll.refreshing,
    reload: poll.reload,
    transport: overlay != null && transport === "sse" ? "sse" : transport,
  };
}

/** GET /locations/stream (event: locations) + poll /locations/latest. */
export function useLocationsLive(everyMs = 20000, limit = 64, enabled = true) {
  const on = enabled !== false;
  return useSseWithPoll({
    streamOp: on ? "get__locations_stream" : "",
    streamQuery: {},
    pollPath: on ? path("get__locations_latest", { query: { limit } }) : null,
    everyMs,
    onFrame: (event, data, set) => {
      if (event !== "locations" && event !== "message") return;
      const json = JSON.parse(data);
      if (json && Array.isArray(json.items)) set(json);
    },
  });
}

/** GET /events/stream + poll GET /events — rolling window, no invent. */
export function useEventsLive(everyMs = 15000) {
  const [sseItems, setSseItems] = useState(null);
  const poll = usePolled(path("get__events", { query: { limit: 40 } }), everyMs);
  const [transport, setTransport] = useState("poll");

  useEffect(() => {
    let dead = false;
    let ac = null;
    let retryTimer = null;
    let attempt = 0;

    const connect = async () => {
      if (dead) return;
      ac = new AbortController();
      const url = apiBase() + path("get__events_stream", { query: { backlog: 40 } });
      try {
        const r = await fetch(url, {
          method: "GET",
          credentials: "include",
          headers: {
            Accept: "text/event-stream",
            "Cache-Control": "no-store",
          },
          signal: ac.signal,
        });
        if (!r.ok || !r.body || typeof r.body.getReader !== "function") {
          throw new Error(String(r.status || "stream unsupported"));
        }
        attempt = 0;
        if (!dead) setTransport("sse");
        const reader = r.body.getReader();
        const dec = new TextDecoder();
        let buf = "";
        while (!dead) {
          const { done, value } = await reader.read();
          if (done) break;
          buf += dec.decode(value, { stream: true });
          const parts = buf.split("\n\n");
          buf = parts.pop() || "";
          for (const chunk of parts) {
            let event = "message";
            let data = "";
            for (const line of chunk.split("\n")) {
              if (line.startsWith("event:")) event = line.slice(6).trim();
              else if (line.startsWith("data:")) data += (data ? "\n" : "") + line.slice(5).trim();
            }
            if (event === "ping" || !data) continue;
            try {
              const json = JSON.parse(data);
              if (!json || typeof json !== "object") continue;
              if (json.event_type === "stream.hello") continue;
              const id = String(json.id || json.event_id || json.ts_epoch_ms || "").trim();
              if (!dead) {
                setSseItems((prev) => {
                  const base = Array.isArray(prev) ? prev : [];
                  if (id && base.some((x) => String(x.id || x.event_id || x.ts_epoch_ms) === id)) {
                    return base;
                  }
                  return [json, ...base].slice(0, 40);
                });
              }
            } catch (_) { /* ignore */ }
          }
        }
        if (!dead) setTransport("poll");
      } catch (e) {
        if (!dead && !(e && e.name === "AbortError")) setTransport("poll");
      }
      if (!dead) {
        const wait = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
        attempt += 1;
        retryTimer = setTimeout(connect, wait);
      }
    };

    connect();
    return () => {
      dead = true;
      if (ac) ac.abort();
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [everyMs]);

  const data = sseItems
    ? { items: sseItems }
    : poll.data;

  return {
    data,
    error: sseItems ? "" : (poll.error || ""),
    loading: !data && poll.loading,
    refreshing: poll.refreshing,
    reload: poll.reload,
    transport: sseItems ? "sse" : transport,
  };
}

/** Camera/AI/dispatch domain events only; excludes tenant auth/system noise. */
export function useCameraSignalsLive(everyMs = 12000, deviceId = "") {
  const query = deviceId ? { device_id: deviceId } : {};
  return useSseWithPoll({
    streamOp: "get__cameras_signals_stream",
    streamQuery: { ...query, backlog: 40 },
    pollPath: path("get__cameras_signals", { query: { ...query, limit: 60 } }),
    everyMs,
    onFrame: (_event, data, set) => {
      const json = JSON.parse(data);
      if (!json || typeof json !== "object" || json.event_type === "stream.hello") return;
      const id = String(json.id || json.event_id || json.ts_epoch_ms || "");
      set((previous) => {
        const base = previous && Array.isArray(previous.items) ? previous.items : [];
        if (id && base.some((row) => String(row.id || row.event_id || row.ts_epoch_ms || "") === id)) {
          return previous;
        }
        return { items: [json, ...base].slice(0, 60), count: Math.min(base.length + 1, 60) };
      });
    },
  });
}
